import {
  Button,
  Col,
  ControlLabel,
  Form,
  FormControl,
  FormGroup,
  ListGroup,
  ListGroupItem,
} from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import DatePicker from 'react-datepicker';
import React, { FormEvent, useCallback, useState } from 'react';
import validation from '@upstart/harmony/lib/validation';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import { Deal, TradeIn } from '@upstart/harmony/lib/legos/dealTypes';
import { debounce, omit } from 'lodash';
import { fmtSocial } from '@upstart/harmony/lib/formatter';
import {
  LossExpressLender,
  LossExpressSearchRequest,
} from '@upstart/harmony/lib/legos/lossExpressTypes';
import moment from 'moment';
import { FAUX_TRADE_IN_ID } from '@upstart/harmony/lib/legos/constants';
import { maskPhone } from '../../../util/utils';
import AddressInput from '../../GlobalUI/AddressInput';

import './TradePayoffModal.scss';
import LossExpressService from '../../../services/LossExpressService';
import {
  TRADE_PAYOFF_FIELD_DISPLAY,
  TradeFinanceType,
  TradePayoffFields,
  TradePayoffFormData,
} from './types';

interface TradePayoffFormProps {
  dealer: Dealer;
  deal: Deal;
  tradeIn?: TradeIn;
  onSubmit: (data: LossExpressSearchRequest) => void;
  onClose: () => void;
}

const TradePayoffForm = (props: TradePayoffFormProps) => {
  const getInitialFormValues = (): TradePayoffFormData => ({
    financeType: TradeFinanceType.LOAN,
    firstName: props.deal.applicant?.firstName ?? '',
    lastName: props.deal.applicant?.lastName ?? '',
    customerAddress: props.deal.applicant?.address ?? '',
    customerPhoneNumber: props.deal.applicant?.phone
      ? maskPhone(props.deal.applicant.phone)
      : '',
    vin:
      props.tradeIn?.id && props.tradeIn.id !== FAUX_TRADE_IN_ID
        ? props.tradeIn.id
        : '',
    customerBirthdate: props.deal.applicant?.birthday
      ? new Date(props.deal.applicant.birthday)
      : undefined,
    socialSecurityNumber: props.deal.applicant?.socialSec
      ? fmtSocial(props.deal.applicant.socialSec)
      : '',
    lenderName: '',
    lenderId: '',
  });

  const [payoffForm, setPayoffForm] = useState<TradePayoffFormData>(
    getInitialFormValues(),
  );
  const [availableLenders, setAvailableLenders] = useState<LossExpressLender[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const fetchLenders = useCallback(
    debounce(async (value: string) => {
      setIsLoading(true);
      const { lenders } = await LossExpressService.getLenders(
        value.replace(/\W/g, ''),
      );
      const integratedLenders = lenders?.filter(
        (l: LossExpressLender) => l.integrated,
      );
      setAvailableLenders(integratedLenders);
      setIsLoading(false);
    }, 500),
    [],
  );

  const handleLenderSearch = (search: string) => {
    if (search.length > 2) {
      fetchLenders(search);
    }
  };

  const updateForm = (field: string) => (update: FormEvent<FormControl>) => {
    let value = (update.target as HTMLInputElement).value;

    if (field === TradePayoffFields.PHONE_NUMBER) {
      value = maskPhone(value);
    } else if (field === TradePayoffFields.SSN) {
      value = fmtSocial(value);
    }

    setPayoffForm({ ...payoffForm, [field]: value });
  };

  const formIsValid = (): boolean => {
    const requiredFields = [
      'financeType',
      'firstName',
      'lastName',
      'customerPhoneNumber',
      'customerAddress',
      'vin',
      'lenderName',
    ];
    const errors: string[] = [];

    Object.entries(payoffForm).forEach(([key, value]) => {
      if (requiredFields.includes(key) && !value) {
        errors.push(
          `${(TRADE_PAYOFF_FIELD_DISPLAY as any)[key]} is a required field.`,
        );
      }
      if (
        key === TradePayoffFields.SSN &&
        payoffForm.socialSecurityNumber &&
        !validation.isSecurityNumber(payoffForm.socialSecurityNumber)
      ) {
        errors.push(
          `${TRADE_PAYOFF_FIELD_DISPLAY[TradePayoffFields.SSN]} is not valid`,
        );
      } else if (
        key === TradePayoffFields.VIN &&
        payoffForm.vin &&
        !validation.isVin(payoffForm.vin)
      ) {
        errors.push(
          `${TRADE_PAYOFF_FIELD_DISPLAY[TradePayoffFields.VIN]} is not valid.`,
        );
      } else if (
        key === TradePayoffFields.BIRTH_DATE &&
        payoffForm.customerBirthdate &&
        !validation.isBirthdayYearFirst(
          moment(payoffForm.customerBirthdate).format('YYYY/MM/DD'),
        )
      ) {
        errors.push(
          `${
            TRADE_PAYOFF_FIELD_DISPLAY[TradePayoffFields.BIRTH_DATE]
          } is not valid`,
        );
      } else if (
        key === TradePayoffFields.PHONE_NUMBER &&
        payoffForm.customerPhoneNumber &&
        !validation.isPhone(payoffForm.customerPhoneNumber)
      ) {
        errors.push(
          `${
            TRADE_PAYOFF_FIELD_DISPLAY[TradePayoffFields.PHONE_NUMBER]
          } is not valid`,
        );
      }
    });

    setValidationErrors([...errors]);
    return errors.length === 0;
  };

  const onSubmit = () => {
    const getPayoffRequest = (): LossExpressSearchRequest => ({
      ...omit(payoffForm, [
        TradePayoffFields.FIRST_NAME,
        TradePayoffFields.LAST_NAME,
      ]),
      customerName: `${payoffForm.firstName} ${payoffForm.lastName}`,
      customerPhoneNumber: payoffForm.customerPhoneNumber.replace(/\D+/g, ''),
      entityName: props.dealer.name ?? '',
      accountNumber: payoffForm.accountNumber || undefined,
      socialSecurityNumber: payoffForm.socialSecurityNumber || undefined,
      customerBirthdate:
        payoffForm.customerBirthdate &&
        moment(payoffForm.customerBirthdate).format('YYYY-MM-DD'),
      vehicleMileage: Number(props.tradeIn?.mileage ?? 0),
      financeType:
        payoffForm.financeType === TradeFinanceType.LEASE ? 'Lease' : 'Retail',
    });

    setValidationErrors([]);
    if (formIsValid()) {
      setIsValid(true);
      props.onSubmit(getPayoffRequest());
    } else {
      setIsValid(false);
    }
  };

  return (
    <div className="payoff-form-container">
      <h3>Get Customer Trade Payoff</h3>
      <h5>Enter all customer information to get most accurate trade payoff</h5>
      <br />
      <Form horizontal onSubmit={onSubmit}>
        <FormGroup>
          <Col xs={4}>
            <ControlLabel>Trade Finance Type *</ControlLabel>
            <FormControl
              data-testid="TradePayoffForm-financeType"
              required
              componentClass="select"
              value={payoffForm.financeType}
              onChange={updateForm(TradePayoffFields.FINANCE_TYPE)}
            >
              {Object.values(TradeFinanceType).map(f => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </FormControl>
          </Col>
        </FormGroup>
        <FormGroup>
          <Col xs={4}>
            <ControlLabel>First Name *</ControlLabel>
            <FormControl
              data-testid="TradePayoffForm-firstName"
              required
              componentClass="input"
              type="text"
              value={payoffForm.firstName}
              onChange={updateForm(TradePayoffFields.FIRST_NAME)}
            />
          </Col>
          <Col xs={4}>
            <ControlLabel>Last Name *</ControlLabel>
            <FormControl
              data-testid="TradePayoffForm-lastName"
              required
              componentClass="input"
              type="text"
              value={payoffForm.lastName}
              onChange={updateForm(TradePayoffFields.LAST_NAME)}
            />
          </Col>
          <Col xs={4}>
            <ControlLabel>Phone *</ControlLabel>
            <FormControl
              data-testid="TradePayoffForm-phone"
              required
              componentClass="input"
              type="text"
              value={payoffForm.customerPhoneNumber}
              onChange={updateForm(TradePayoffFields.PHONE_NUMBER)}
            />
          </Col>
        </FormGroup>
        <FormGroup>
          <Col xs={8}>
            <AddressInput
              testId="TradePayoffForm-address"
              label="Street Address"
              value={payoffForm.customerAddress}
              required
              onChange={(value: string) =>
                setPayoffForm({ ...payoffForm, customerAddress: value })
              }
            />
          </Col>
        </FormGroup>
        <FormGroup>
          <Col xs={4}>
            <ControlLabel>VIN *</ControlLabel>
            <FormControl
              data-testid="TradePayoffForm-vin"
              required
              componentClass="input"
              type="text"
              disabled={Boolean(props.tradeIn?.id)}
              maxLength={17}
              value={payoffForm.vin}
              onChange={updateForm(TradePayoffFields.VIN)}
            />
          </Col>
        </FormGroup>
        <FormGroup>
          <Col xs={8}>
            <ControlLabel>Lender/Lease Company *</ControlLabel>
            <Typeahead
              id="trade-payoff-typeahead"
              inputProps={{
                // @ts-ignore
                'data-testid': 'TradePayoffForm-lender',
              }}
              options={availableLenders}
              labelKey="lenderName"
              onInputChange={handleLenderSearch}
              onChange={(selectedLender: LossExpressLender[]) => {
                const lender = selectedLender[0];
                setPayoffForm({
                  ...payoffForm,
                  lenderId: lender?.lenderId,
                  lenderName: lender?.lenderName,
                });
              }}
              isLoading={isLoading}
              defaultInputValue={payoffForm.lenderName}
            />
          </Col>
          <Col xs={4}>
            <ControlLabel>Loan/Lease Account Number</ControlLabel>
            <FormControl
              data-testid="TradePayoffForm-accountNumber"
              componentClass="input"
              type="text"
              value={payoffForm.accountNumber}
              maxLength={40}
              onChange={updateForm(TradePayoffFields.ACCOUNT_NUMBER)}
            />
          </Col>
        </FormGroup>
        <FormGroup>
          <Col xs={4}>
            <ControlLabel>Birth Date (optional)</ControlLabel>
            <DatePicker
              customInput={
                <input
                  type="text"
                  value={moment(payoffForm.customerBirthdate).format(
                    'MM/DD/YYYY',
                  )}
                  data-testid="TradePayoffForm-birthDate"
                />
              }
              selected={
                payoffForm.customerBirthdate
                  ? new Date(payoffForm.customerBirthdate)
                  : null
              }
              onChange={(date: Date) => {
                setPayoffForm({
                  ...payoffForm,
                  customerBirthdate: date,
                });
              }}
            />
          </Col>
          <Col xs={4}>
            <ControlLabel>Social Security Number (optional)</ControlLabel>
            <FormControl
              data-testid="TradePayoffForm-ssn"
              componentClass="input"
              type="password"
              maxLength={9}
              value={payoffForm.socialSecurityNumber}
              onChange={updateForm(TradePayoffFields.SSN)}
            />
          </Col>
        </FormGroup>
        {!isValid && (
          <div className="error-container">
            <ListGroup>
              {validationErrors.map(e => (
                <ListGroupItem key={e}>{e}</ListGroupItem>
              ))}
            </ListGroup>
          </div>
        )}
        <div className="trade-payoff-footer">
          <Button
            className="btn modal-button secondary cc-button-outlined"
            onClick={props.onClose}
          >
            Cancel
          </Button>
          <Button
            className="btn modal-button cc-button-primary capitalize-in-chairman"
            onClick={onSubmit}
          >
            Start lookup
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default TradePayoffForm;
